/* One service day — how many came, and who to look in on.
 *
 * Counts first; each count that has names behind it opens them in a
 * drawer, a page at a time, searchable. From the absentees, one press
 * writes to them or calls them together — the same composer and the same
 * Meet window as anywhere else, with exactly these people as the audience.
 *
 * WHILE THE DAY IS ON, nobody has missed it yet, so the list says "not yet
 * in" rather than "absent".
 */
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  ChevronRight,
  Loader2,
  Phone,
  Send,
  UserCheck,
  UserMinus,
  UserX,
} from 'lucide-react'
import {
  fetchFollowUpIds,
  useExcuse,
  useFeatures,
  useFollowUp,
  useMe,
  useOccurrenceReport,
} from '@/api/queries'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Drawer } from '@/components/ui/drawer'
import { Field } from '@/components/ui/field'
import { Modal } from '@/components/ui/modal'
import { cn } from '@/lib/utils'
import { useDebounced } from '@/lib/useDebounced'
import {
  ArrangeDialog,
  useArrangeable,
  type ChosenPeople,
} from '@/features/church/ArrangeDialog'
import type { FollowUpKind, FollowUpRow, OccurrenceReport } from '@/types/api'
import { useAttendanceAccess } from './AttendancePage'
import type { Back } from './DayList'
import { Pager, SearchBox, clock, day, familyNote } from './parts'

const PAGE_SIZE = 25

const METHOD: Record<string, string> = {
  self: 'checked in themselves',
  family: 'checked in by family',
  usher: 'marked by an usher',
}

export function DayReportPage() {
  const { occurrenceId } = useParams<{ occurrenceId: string }>()
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null
  const access = useAttendanceAccess(churchId)
  const report = useOccurrenceReport(churchId, occurrenceId)
  const [list, setList] = useState<FollowUpKind | null>(null)
  // Back to wherever the day was opened from: the dashboard, the list of
  // days, or one service's own page.
  const from = (useLocation().state as Back | null) ?? {
    back: '/attendance/days',
    backLabel: 'Service days',
  }

  if (report.isLoading) {
    return (
      <Card className="flex justify-center py-s6">
        <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
      </Card>
    )
  }
  if (report.isError || !report.data || !churchId) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        {(report.error as Error)?.message || 'That service day is not available.'}
      </Card>
    )
  }

  const r = report.data
  const c = r.counts
  const canFollowUp = access.manage
  const methods = Object.entries(r.by_method).filter(([, n]) => n > 0)

  return (
    <div className="space-y-s4">
      <div>
        <Link
          to={from.back}
          className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          {from.backLabel}
        </Link>
        <h1 className="mt-s2 text-xl font-extrabold tracking-tight">{r.name}</h1>
        <p className="mt-0.5 text-base text-ink-2">
          {day(r.date, true)}
          {r.group_name ? ` · ${r.group_name}` : ''}
          {r.over ? '' : ' · still on'}
        </p>
      </div>

      <Card className="p-s4">
        <div className="flex flex-wrap gap-x-s6 gap-y-s3">
          <Stat value={c.expected} label="expected" />
          <Stat value={c.present} label="present" tone="success" />
          <Stat value={c.excused} label="excused" />
          <Stat value={r.over ? c.absent : c.not_yet} label={r.over ? 'absent' : 'not yet in'} />
          <Stat value={c.visitors} label="visitors" />
        </div>
        {r.slots.length > 1 || methods.length ? (
          <div className="mt-s3 space-y-1 border-t border-line-2 pt-s3 text-sm text-ink-2">
            {r.slots.length > 1 ? (
              <p>
                {r.slots
                  .map((s) => `${clock(s.starts_at)} ${s.label}: ${s.present}`)
                  .join(' · ')}
              </p>
            ) : null}
            {methods.length ? (
              <p>
                {methods.map(([k, n]) => `${n} ${METHOD[k] ?? k}`).join(' · ')}
              </p>
            ) : null}
          </div>
        ) : null}
      </Card>

      {canFollowUp ? (
        <>
          <Card className="space-y-s3 p-s4">
            <h2 className="text-md font-bold">Follow up</h2>
            <div className="grid gap-s3 sm:grid-cols-2">
              <Tile
                count={r.over ? c.absent : c.not_yet}
                label={r.over ? 'Absent' : 'Not yet in'}
                icon={UserX}
                onClick={() => setList('absent')}
              />
              <Tile
                count={c.excused}
                label="Excused"
                icon={UserMinus}
                onClick={() => setList('excused')}
              />
              {r.rsvp_going_absent !== null ? (
                <Tile
                  count={r.rsvp_going_absent}
                  label="Said going but did not come"
                  icon={UserCheck}
                  onClick={() => setList('rsvp_no_show')}
                />
              ) : null}
            </div>
            <FollowUpActions churchId={churchId} report={r} kind="absent" count={r.over ? c.absent : c.not_yet} />
          </Card>

          <FollowUpDrawer
            churchId={churchId}
            report={r}
            kind={list}
            onKind={setList}
          />
        </>
      ) : null}
    </div>
  )
}

/** Message absentees · Meet absentees. */
export function FollowUpActions({
  churchId,
  report: r,
  kind,
  count,
}: {
  churchId: string
  report: Pick<OccurrenceReport, 'id' | 'name' | 'date' | 'over'>
  kind: FollowUpKind
  count: number
}) {
  const navigate = useNavigate()
  const [busy, setBusy] = useState<'message' | 'meet' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [meet, setMeet] = useState<ChosenPeople | null>(null)
  /* A message to chosen people, and a meeting, are each the church's to
     offer. */
  const mayMessage = useFeatures().send_individual_messages
  const mayMeet = useArrangeable().meet

  if (count === 0 || (!mayMessage && !mayMeet)) return null

  const who = kind === 'rsvp_no_show' ? 'said going but did not come' : r.over ? 'absent' : 'not yet in'

  async function people(): Promise<ChosenPeople | null> {
    const ids = await fetchFollowUpIds(churchId, r.id, kind)
    if (!ids.length) {
      setError('Nobody is on this list any more.')
      return null
    }
    return {
      ids,
      label: `${ids.length} ${ids.length === 1 ? 'person' : 'people'} ${who} · ${r.name}, ${day(r.date)}`,
    }
  }

  async function go(action: 'message' | 'meet') {
    setError(null)
    setBusy(action)
    try {
      const chosen = await people()
      if (!chosen) return
      if (action === 'message') navigate('/messages', { state: { people: chosen } })
      else setMeet(chosen)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusy(null)
    }
  }

  const noun = kind === 'rsvp_no_show' ? 'them' : 'absentees'

  return (
    <div className="space-y-s2">
      <div className="flex flex-wrap gap-s2">
        {mayMessage ? (
          <Button variant="primary" onClick={() => go('message')} loading={busy === 'message'}>
            <Send className="h-4 w-4" aria-hidden />
            Message {noun}
          </Button>
        ) : null}
        {mayMeet ? (
          <Button
            variant={mayMessage ? 'secondary' : 'primary'}
            onClick={() => go('meet')}
            loading={busy === 'meet'}
          >
            <Phone className="h-4 w-4" aria-hidden />
            Meet {noun}
          </Button>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      {meet ? (
        <ArrangeDialog
          mode="meet"
          churchId={churchId}
          people={meet}
          initialTitle={`Missed you at ${r.name}`}
          onClose={() => setMeet(null)}
        />
      ) : null}
    </div>
  )
}

const KIND_TITLE: Record<FollowUpKind, string> = {
  absent: 'Absent',
  excused: 'Excused',
  rsvp_no_show: 'Said going, did not come',
}

function FollowUpDrawer({
  churchId,
  report: r,
  kind,
  onKind,
}: {
  churchId: string
  report: OccurrenceReport
  kind: FollowUpKind | null
  onKind: (k: FollowUpKind | null) => void
}) {
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [excusing, setExcusing] = useState<FollowUpRow | null>(null)
  const q = useDebounced(query.trim())
  useEffect(() => setPage(1), [kind, q])
  useEffect(() => {
    if (kind === null) setQuery('')
  }, [kind])

  const list = useFollowUp(
    churchId,
    r.id,
    { kind: kind ?? 'absent', q: q || undefined, page, pageSize: PAGE_SIZE },
    kind !== null,
  )
  const excuse = useExcuse(churchId, r.id)
  const data = list.data
  const rows = data?.items ?? []
  const kinds: FollowUpKind[] = ['absent', 'excused', ...(r.rsvp_going_absent !== null ? (['rsvp_no_show'] as const) : [])]
  const absentWord = r.over ? 'Absent' : 'Not yet in'

  return (
    <Drawer
      open={kind !== null}
      onOpenChange={(o) => (o ? null : onKind(null))}
      title={`${r.name} · ${day(r.date)}`}
      description={
        data && !data.final ? 'The day is still on — this can change until it ends.' : undefined
      }
      footer={
        data && (data.total > PAGE_SIZE || (kind !== 'excused' && data.total > 0)) ? (
          <div className="space-y-s3">
            <Pager page={page} total={data.total} pageSize={PAGE_SIZE} onPage={setPage} />
            {kind && kind !== 'excused' && !q ? (
              <FollowUpActions churchId={churchId} report={r} kind={kind} count={data.total} />
            ) : null}
          </div>
        ) : null
      }
    >
      <div className="sticky top-0 z-10 space-y-s3 border-b border-line-2 bg-surface px-s4 py-s3">
        <div role="tablist" className="flex flex-wrap gap-s2">
          {kinds.map((k) => (
            <button
              key={k}
              role="tab"
              aria-selected={kind === k}
              onClick={() => onKind(k)}
              className={cn(
                'rounded-pill border px-s3 py-1.5 text-sm font-semibold transition-colors',
                kind === k
                  ? 'border-brand bg-brand-soft text-brand'
                  : 'border-line-2 text-ink-2 hover:bg-hover',
              )}
            >
              {k === 'absent' ? absentWord : KIND_TITLE[k]}
            </button>
          ))}
        </div>
        <SearchBox value={query} onChange={setQuery} label="Search this list" />
      </div>

      {excuse.isError ? (
        <p role="alert" className="mx-s4 mt-s3 rounded-md bg-danger-soft px-s3 py-s2 text-sm text-danger">
          {(excuse.error as Error).message}
        </p>
      ) : null}

      {list.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : list.isError ? (
        <p className="px-s4 py-s6 text-center text-base text-ink-2">
          {(list.error as Error).message}
        </p>
      ) : rows.length === 0 ? (
        <p className="px-s4 py-s6 text-center text-base text-ink-2">
          {q ? `Nobody here matches “${q}”.` : 'Nobody here.'}
        </p>
      ) : (
        <ul className={cn('divide-y divide-line-2', list.isFetching && 'opacity-70')}>
          {rows.map((p) => {
            const note = familyNote(p.household_of, p.relation)
            return (
              <li key={p.id} className="flex flex-wrap items-center gap-s3 px-s4 py-s3">
                <Avatar name={p.name} src={p.avatar_url} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-semibold">{p.name}</p>
                  <p className="truncate text-sm text-ink-2">
                    {[
                      note,
                      p.mobile,
                      kind === 'excused'
                        ? p.excuse_kind === 'away'
                          ? `Said they’d be away${p.reason ? ` — ${p.reason}` : ''}`
                          : p.reason || 'Excused'
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                {kind !== 'excused' && p.missed_in_a_row >= 2 ? (
                  <span className="shrink-0 rounded-pill bg-warning-soft px-s2 py-0.5 text-xs font-semibold text-warning-ink">
                    Missed last {p.missed_in_a_row}
                  </span>
                ) : null}
                {kind === 'absent' ? (
                  <Button size="sm" variant="secondary" onClick={() => setExcusing(p)}>
                    Excuse
                  </Button>
                ) : kind === 'excused' && p.excuse_kind === 'pastor' ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={excuse.isPending && excuse.variables?.userId === p.id}
                    onClick={() => excuse.mutate({ userId: p.id, reason: false })}
                  >
                    Undo
                  </Button>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      {excusing ? (
        <ExcuseDialog
          name={excusing.name}
          loading={excuse.isPending}
          error={excuse.isError ? (excuse.error as Error).message : null}
          onClose={() => {
            excuse.reset()
            setExcusing(null)
          }}
          onSave={(reason) =>
            excuse.mutate(
              { userId: excusing.id, reason: reason || null },
              { onSuccess: () => setExcusing(null) },
            )
          }
        />
      ) : null}
    </Drawer>
  )
}

function ExcuseDialog({
  name,
  loading,
  error,
  onClose,
  onSave,
}: {
  name: string
  loading: boolean
  error: string | null
  onClose: () => void
  onSave: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Excuse ${name.split(' ')[0]}`}
      width="420px"
      footer={
        <Button variant="primary" size="lg" full loading={loading} onClick={() => onSave(reason.trim())}>
          Excuse
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        <Field
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 300))}
          placeholder="Unwell, travelling, working"
          autoFocus
        />
        {error ? <p className="text-sm text-danger">{error}</p> : null}
      </div>
    </Modal>
  )
}

function Stat({ value, label, tone }: { value: number; label: string; tone?: 'success' }) {
  return (
    <div>
      <div className={cn('text-lg font-extrabold tracking-tight', tone === 'success' && 'text-success-ink')}>
        {value}
      </div>
      <div className="text-sm text-ink-2">{label}</div>
    </div>
  )
}

function Tile({
  count,
  label,
  icon: Icon,
  onClick,
}: {
  count: number
  label: string
  icon: React.ComponentType<{ className?: string }>
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={count === 0}
      className="flex items-center gap-s3 rounded border border-line-2 bg-surface px-s4 py-s3 text-left transition-colors hover:bg-hover disabled:cursor-default disabled:hover:bg-surface"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-pill bg-surface-2 text-ink-2">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-lg font-extrabold tracking-tight">{count}</span>
        <span className="block text-sm text-ink-2">{label}</span>
      </span>
      {count > 0 ? (
        <span className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-brand">
          See who
          <ChevronRight className="h-4 w-4" aria-hidden />
        </span>
      ) : null}
    </button>
  )
}

